import { ScrollableTabsNavigation, TabsNavigationItemData } from "client/Components";
import {
	ReactElement,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import styles from "./MobileNavigationBar.module.less";
import { TileGroupRenderState } from "../RenderState/common";
import { DatabaseOrTempObjectId } from "client_legacy/ndaw/tempOrDatabaseId";
import { isNil } from "client/Misc/utils";
import { GridDimension } from "client/Api/Workspace/common";
import { convertDimensionToPx, GridCellPixelValues } from "../GridCellPixelValuesTracker";

export type MobileNavigationBarBridgeProps = {
	sortedVisibleTileGroups: readonly TileGroupRenderState[];
	scrollContainer: HTMLElement;
	tileGroupsContainer: HTMLElement;
	tileGroupGridCellPixelValues: GridCellPixelValues;
	onScrollToTileGroup: (id: DatabaseOrTempObjectId, groups: readonly TileGroupRenderState[]) => void;
};

export const MobileNavigationBar = (props: MobileNavigationBarBridgeProps): ReactElement => {
	const {
		sortedVisibleTileGroups,
		scrollContainer,
		tileGroupsContainer,
		tileGroupGridCellPixelValues,
		onScrollToTileGroup
	} = props;

	const [verticalScrollPositionInTileGroupGrid, setVerticalScrollPositionInTileGroupGrid] = useState<number>(0);

	const { rowGutter, rowHeightWithGutter } = tileGroupGridCellPixelValues;

	const activeRow = useMemo(
		(): number => {
			return Math.floor(
				(verticalScrollPositionInTileGroupGrid + (rowGutter / 2)) / rowHeightWithGutter
			);
		},
		[
			rowGutter,
			rowHeightWithGutter,
			verticalScrollPositionInTileGroupGrid
		]
	);

	const activeTabId = useMemo(
		(): DatabaseOrTempObjectId | undefined => {
			if (sortedVisibleTileGroups.length === 0) {
				return undefined;
			}

			let index: number = -1;

			for (let i = 1; i < sortedVisibleTileGroups.length; i++) {
				const current: TileGroupRenderState = sortedVisibleTileGroups[i];
				if (current.position.row > activeRow) {
					index = i - 1;
					break;
				}
			}

			if (index === -1) {
				index = sortedVisibleTileGroups.length - 1;
			}

			return sortedVisibleTileGroups[index]
				?.data
				.id;
		},
		[activeRow, sortedVisibleTileGroups]
	);

	const tabsData = useMemo(
		(): readonly TabsNavigationItemData<DatabaseOrTempObjectId>[] => {
			return sortedVisibleTileGroups
				.map((tileGroup): TabsNavigationItemData<DatabaseOrTempObjectId> => {
					const { id, name } = tileGroup.data;

					return {
						text: name,
						value: id,
						trackingId: `tile-group-navigation_item|${id.hasDatabaseId ? id.databaseId : id.tempId}`
					};
				});
		},
		[sortedVisibleTileGroups]
	);

	const groupTabsChangeHandler = useCallback(
		(id: DatabaseOrTempObjectId): void => {
			onScrollToTileGroup(id, sortedVisibleTileGroups);
		},
		[onScrollToTileGroup, sortedVisibleTileGroups]
	);

	useEffect(
		(): (() => void) => {
			const cancellation = new AbortController();

			scrollContainer.addEventListener(
				"scroll",
				() => {
					const { top: scrollContainerTop } = scrollContainer.getBoundingClientRect();
					const { top: tileGroupsContainerTop } = tileGroupsContainer.getBoundingClientRect();

					setVerticalScrollPositionInTileGroupGrid(scrollContainerTop - tileGroupsContainerTop);
				},
				{ signal: cancellation.signal }
			);

			return (): void => cancellation.abort();
		},
		[scrollContainer, tileGroupsContainer]
	);

	return (
		<div className={styles.root}>
			<ScrollableTabsNavigation
				value={activeTabId}
				items={tabsData}
				onChange={groupTabsChangeHandler}
				areEqual={(a, b) => !isNil(b) && a.equals(b)}
			/>
		</div>
	);
};
MobileNavigationBar.displayName = "MobileNavigationBar";

type MobileNavigationBarExtraScrollSpacingProps = {
	sortedVisibleTileGroups: readonly TileGroupRenderState[];
	scrollContainerHeight: number;
	tileGroupGridCellPixelValues: GridCellPixelValues;
};

const MobileNavigationBarExtraScrollSpacing = (props: MobileNavigationBarExtraScrollSpacingProps): ReactElement => {
	const {
		sortedVisibleTileGroups,
		scrollContainerHeight,
		tileGroupGridCellPixelValues
	} = props;

	const height = useMemo(
		(): number => {
			if (sortedVisibleTileGroups.length === 0) {
				return 0;
			}

			const lastTileGroupDimension: GridDimension = sortedVisibleTileGroups[sortedVisibleTileGroups.length - 1]
				.dimension;

			const lastTileGroupHeight: number = convertDimensionToPx(
				lastTileGroupDimension,
				tileGroupGridCellPixelValues
			)
				.height;

			return Math.max(0, scrollContainerHeight - lastTileGroupHeight);
		},
		[
			scrollContainerHeight,
			sortedVisibleTileGroups,
			tileGroupGridCellPixelValues
		]
	);

	return (
		<div
			className={styles.extraScrollSpacing}
			style={{ height: height }}
		/>
	);
};
MobileNavigationBarExtraScrollSpacing.displayName = "MobileNavigationBar2.ExtraScrollSpacing";

MobileNavigationBar.ExtraScrollSpacing = MobileNavigationBarExtraScrollSpacing;
